import React, { Component } from 'react';

import range from 'lodash.range';
import PropTypes from 'prop-types';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import styled from 'styled-components';

import { defineMessages, injectIntl } from '@techstyle/react-intl';

import { BasicButton } from '../../styles/button';

const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';

const messages = defineMessages({
  previous: {
    defaultMessage: 'Previous',
    id: 'global_button.previous',
  },
  next: {
    defaultMessage: 'Next',
    id: 'global_button.next',
  },
});

const PaginationContainer = styled.div`
  display: flex;
`;

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  flex: 1 1 auto;
`;

const PageButton = styled(BasicButton)`
  min-width: 8px;
  background-color: transparent;
  border: none;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1.25px;
  text-align: center;
  padding: 0px ${props => props.theme.spacing.tiny}px;
  color: ${props => props.theme.colors.active};

  &:focus {
    outline: none;
  }
`;

const PageButtonLabel = styled.div`
  border-bottom: ${props => (props.isCurrent ? '2px solid' : 'none')};
  margin-top: ${props => (props.isCurrent ? 2 : 0)}px;
`;

const ArrowButton = styled(PageButton)`
  flex: 0 0 10px;
  &:focus {
    outline: none;
  }
`;

const RightIcon = styled(FaChevronRight)`
  font-size: 14px;
  margin-top: 2px;
`;

const LeftIcon = styled(FaChevronLeft)`
  font-size: 14px;
  margin-top: 2px;
`;

class Pagination extends Component {
  static propTypes = {
    totalRecords: PropTypes.number.isRequired,
    pageLimit: PropTypes.number,
    onPageChanged: PropTypes.func,
  };

  static defaultProps = {
    pageLimit: 5,
  };

  constructor(props) {
    super(props);
    const { totalRecords = null, pageLimit = 5 } = props;

    this.totalPages = Math.ceil(parseInt(totalRecords, 10) / pageLimit);

    this.state = { currentPage: 1 };
  }

  componentDidMount() {
    this.gotoPage(1, false);
  }

  gotoPage = (page, shouldOnPageChanged = true) => {
    const { onPageChanged, pageLimit, totalRecords } = this.props;

    const currentPage = Math.max(0, Math.min(page, this.totalPages));

    const paginationData = {
      currentPage,
      totalPages: this.totalPages,
      pageLimit: pageLimit,
      totalRecords: totalRecords,
    };

    this.setState({ currentPage });
    if (shouldOnPageChanged) {
      onPageChanged(paginationData);
    }
  };

  handleClick = (page, evt) => {
    evt.preventDefault();
    this.gotoPage(page);
  };

  handleMoveLeft = evt => {
    evt.preventDefault();
    this.gotoPage(this.state.currentPage - 1);
  };

  handleMoveRight = evt => {
    evt.preventDefault();
    this.gotoPage(this.state.currentPage + 1);
  };

  fetchPageNumbers = () => {
    const totalPages = this.totalPages;
    const currentPage = this.state.currentPage;

    // number of page buttons to display
    const totalBlocks = 3;

    if (totalPages > totalBlocks) {
      const leftBound = currentPage - 1;
      const rightBound = currentPage + 1;
      const beforeLastPage = totalPages - 1;

      const startPage = leftBound < 2 ? 1 : leftBound;
      const endPage =
        rightBound <= beforeLastPage
          ? leftBound < 2
            ? 3
            : rightBound
          : totalPages;

      let pages = range(startPage, endPage + 1);
      /**
       * hasLeftPages: has hidden pages to the left
       * hasRightPages: has hidden pages to the right
       */
      const hasLeftPages = startPage >= 1 && currentPage > 1;
      const hasRightPages =
        totalPages - endPage >= 0 && currentPage < totalPages;

      switch (true) {
        case hasLeftPages && !hasRightPages: {
          pages = [LEFT_PAGE, ...pages];
          break;
        }

        case !hasLeftPages && hasRightPages: {
          pages = [...pages, RIGHT_PAGE];
          break;
        }

        case hasLeftPages && hasRightPages:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
          break;
        }
      }
      return pages;
    }

    return range(1, totalPages + 1);
  };

  render() {
    const { totalRecords, intl } = this.props;

    if (!totalRecords) {
      return null;
    }

    if (this.totalPages === 1) {
      return null;
    }

    const { currentPage } = this.state;
    const pages = this.fetchPageNumbers();
    const showRightButton = pages.includes(RIGHT_PAGE);
    const showLeftButton = pages.includes(LEFT_PAGE);

    return (
      <PaginationContainer>
        {showLeftButton && (
          <ArrowButton
            aria-label={intl.formatMessage(messages.previous)}
            onClick={this.handleMoveLeft}
          >
            <LeftIcon />
          </ArrowButton>
        )}
        <PageContainer>
          {pages.map((page, index) => {
            if (page !== LEFT_PAGE && page !== RIGHT_PAGE) {
              return (
                <PageButton
                  key={index}
                  onClick={e => this.handleClick(page, e)}
                >
                  <PageButtonLabel isCurrent={currentPage === page}>
                    {page}
                  </PageButtonLabel>
                </PageButton>
              );
            }
            return null;
          })}
        </PageContainer>
        {showRightButton && (
          <ArrowButton
            aria-label={intl.formatMessage(messages.next)}
            onClick={this.handleMoveRight}
            data-testid="pagination-right"
          >
            <RightIcon />
          </ArrowButton>
        )}
      </PaginationContainer>
    );
  }
}

export default injectIntl(Pagination);
